import React from 'react';
import { Formik, Form } from 'formik';
import ReCAPTCHA from "react-google-recaptcha";
import { validate, defaultSchema } from '../functions/validation';
import API from '../functions/api';
import { gtmPush } from '../functions/gtm';
import sanitizeHtml from 'sanitize-html';
import Button from '../audi-ui-components/Button';
import TextField from '../audi-ui-components/TextField';
import TextArea from '../audi-ui-components/TextArea';
import Select from '../audi-ui-components/Select';
import Dropdown from '../audi-ui-components/Dropdown';
import Checkbox from '../audi-ui-components/Checkbox';
import CheckboxGroup from '../components/CheckboxGroupField';
import RadioFieldset from '../audi-ui-components/RadioFieldset';
import DateField from '../components/DateField';
import Spinner from '../audi-ui-components/Spinner';
import {
    FIELD_SELECT,
    FIELD_DROPDOWN,
    FIELD_TEXT,
    FIELD_TEXTAREA,
    FIELD_CHECKBOX,
    FIELD_CHECKBOX_GROUP,
    FIELD_RADIO,
    FIELD_DATE,
    DEFAULT_THEME,
} from '../constants';

const fieldComponents = {
    [FIELD_SELECT]: Select,
    [FIELD_DROPDOWN]: Dropdown,
    [FIELD_TEXT]: TextField,
    [FIELD_TEXTAREA]: TextArea,
    [FIELD_DATE]: DateField,
    [FIELD_CHECKBOX]: Checkbox,
    [FIELD_CHECKBOX_GROUP]: CheckboxGroup,
    [FIELD_RADIO]: RadioFieldset
};

function walk(o, f, context) {
    f(o, context);
    if(typeof o !== 'object') {
        return context;
    }
    if (o.fields) {
        return o.fields.forEach(e => walk(e, f, context)), context;
    }
    if(Array.isArray(o)) return o.forEach(e => walk(e, f, context)), context;
    return context;
}

class AudiForm extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            capVal: false,
            serverError: null
        };
    }
    
    scrollToError = () => {
      setTimeout(() => {
        let el = document.querySelector(".is-invalid");
        if (el) {
          el.scrollIntoView({behavior: "smooth", block: "center"});
        }
      }, 1000);
    }

    handleSubmit = (values, formikBag) => {
        this.setState({serverError: null});
        const {
            formName, gtmFormName, slug, fields, hiddenFields, onSubmitSuccess, onSubmitError, api, method
        } = this.props;
        const _gtmFormName = typeof gtmFormName === "string" && gtmFormName?.length > 0 ? gtmFormName : formName;
        // sanitize text fields
        for (let i=0; i<fields.length; i++) {
            let f = fields[i];
            if (f.name && f.field && (f.field === FIELD_TEXT || f.field === FIELD_TEXTAREA)) {
                values[f.name] = sanitizeHtml(values[f.name], {allowedTags: [], allowedAttributes: {}});
            }
        }
        // add optOut
        if (values.hasOwnProperty("optIn")) {
            if (values.optIn === "") {
                values.optIn = false;
            }
            values.optOut = !values.optIn;
        }
        // recaptcha
        if (this.props.recaptcha) { values.token = this.state.capVal; }
        // add hidden fields
        let payload = Object.assign({}, values, hiddenFields);

        if (typeof this.props.preSubmit === "function") {
            payload = this.props.preSubmit(payload);
        }

        if (process.env.RAZZLE_APP_ENV !== "local" || payload.submitToBackend) {

            // thneed.trigger(thneed.events.formSubmit, formName, {formData: payload, formName});
            gtmPush("form", "formSubmit", _gtmFormName, payload);

            // submit to backend
            return API({
                url: api,
                method: method,
                data: {
                    formName,
                    slug: slug || "",
                    payload,
                    variation: 1,
                }
            }).then((result) => { // SUCCESS
                formikBag.setSubmitting(false);
                // thneed.trigger(thneed.events.formSubmitSuccess, formName, {formData: payload, formName});
                gtmPush("form", "formSubmitSuccess", _gtmFormName, payload);
                onSubmitSuccess(payload, result.data);
            }).catch((error) => { // FAIL
                // console.log(error.response);
                // thneed.trigger(thneed.events.formSubmitError, formName, {formData: payload, formName});
                gtmPush("form", "formSubmitError", _gtmFormName, payload);
                if (error.response?.data?.modelState) {
                    let errors = {};
                    for (let key in error.response.data.modelState) {
                        if (!Object.hasOwnProperty(key)) {
                            errors[key] = error.response.data.modelState[key][0];
                        }
                    }
                    formikBag.setErrors(errors);
                } else {
                    let msg = "An error has occured";
                    if (error.response?.data?.message) {
                        msg = error.response.data.message;
                    }
                    this.setState({serverError: msg});
                }
                formikBag.setSubmitting(false);
                this.scrollToError();
                onSubmitError();
            });
        } else {
            // fake submit for frontend testing
            console.log(payload);
            formikBag.setSubmitting(false);
            onSubmitSuccess(payload);
        }
    }

    validateForm = (values) => {
        var _schema = {...this.props.schema};
        var fields = Object.keys(values);
        for (let i=0; i<fields.length; i++) {
            let f = fields[i];
            if (!_schema[f] && typeof defaultSchema[f] !== 'undefined') {
                _schema[f] = defaultSchema[f]
            }
        }
        var v = validate(values, _schema, {format: "firstError", fullMessages: false});
        // console.log("validation", values, v);
        return v;
    }

    getInitialValues = () => {
        const {fields} = this.props;
        const v = walk(fields, (x, context) => {
            if(x && x.name) {
                context[x.name] = x.value || "";
            }
        }, {});
        return v;
    }

    renderRow = (rowData, rowIndex, formikBag) => {
        return (
            <div className="row" key={`row_${rowIndex}`}>
                {rowData.map((item, idx) => {
                    if (Array.isArray(item)) {
                        return (
                            <div className="col-12" key={`col_${idx}`}>
                                {this.renderRow(item, `${rowIndex}_${idx}`, formikBag)}
                            </div>
                        );
                    }
                    return (
                        <div className={item.colClass || "col"} key={`col_${idx}`}>
                            {this.renderCell(item, `${rowIndex}_${idx}`, formikBag)}
                        </div>
                    );
                })}
            </div>
        );
    }

    renderCell = (cellData, cellIndex, formikBag) => {
        // multiple fields
        if (cellData.fields) {
            return cellData.fields.map((item, idx) => {
                if (Array.isArray(item)) {
                    return this.renderRow(item, idx, formikBag);
                }
                return this.renderCell(item, idx, formikBag);
            });
        }
        // single field
        if (cellData.field) {
            const Field = fieldComponents[cellData.field];
            if (Field) {
                const {values, errors, touched, setFieldValue, setFieldTouched} = formikBag;
                if (cellData.field === FIELD_CHECKBOX) {
                    cellData.checked = !!values[cellData.name];
                    cellData.value = "";
                    cellData.onChange = (v,e) => {
                        setFieldValue(cellData.name, e.target.checked);
                        if (typeof cellData.onChangeFunction === "function") {
                            cellData.onChangeFunction(v,e);
                        }
                    };
                } else {
                    cellData.value = values[cellData.name];
                    cellData.onChange = (v) => {
                        if (typeof cellData.formatValue === 'function') {
                            v = cellData.formatValue(v);
                        } else if (cellData.name === "emailAddress" || cellData.name === "postCode" || cellData.trim) {
                            v = v.trim();
                        }
                        setFieldValue(cellData.name, v);
                        if (typeof cellData.onChangeFunction === "function") {
                            cellData.onChangeFunction(v);
                        }
                    };
                }
                return (
                    <Field {...cellData}
                        key={`cell_${cellIndex}`}
                        isInvalid={errors[cellData.name] && touched[cellData.name]}
                        invalidMessage={errors[cellData.name]}
                        onBlur={() => { setFieldTouched(cellData.name); }}
                        className={`form-field ${cellData.className || ''}`}
                        theme={this.props.theme === "dark" ? "light" : null}
                    />
                );
            }
            return null;
        }
        // module
        if (cellData.module) {
            const Module = cellData.module;
            return (
                <Module {...cellData} {...formikBag} key={`cell_${cellIndex}`} theme={this.props.theme} />
            );
        }
        // jsx node
        if (cellData.node) {
            return (<div key={`cell_${cellIndex}`}>{cellData.node}</div>);
        }
        // html string
        if (cellData.html) {
            return (<div key={`cell_${cellIndex}`} dangerouslySetInnerHTML={{__html: cellData.html}}></div>);
        }
        return null;
    }

    render() {
        const {fields, style, submitLabel, disabled} = this.props;
        return (
            <Formik validate={this.validateForm} onSubmit={this.handleSubmit} initialValues={this.getInitialValues()}>
                {(formikBag) => (
                    <Form className="audi-form" style={style}>
                        {formikBag.isSubmitting && <div className="spinner-wrapper sticky"><Spinner isContinuous /></div>}

                        {fields.map((item, idx) => {
                            if (Array.isArray(item)) {
                                return this.renderRow(item, idx, formikBag);
                            }
                            return this.renderCell(item, idx, formikBag);
                        })}

                        {this.props.recaptcha && <div className="form-field recaptcha">
                            <ReCAPTCHA sitekey={process.env.RAZZLE_GOOGLE_RECAPTCHA_KEY} onChange={(v) => { this.setState({capVal: v}); }} />
                        </div>}

                        {process.env.RAZZLE_APP_ENV === 'local' && <div className="form-field dev-only">
                            <Checkbox name="submitToBackend" label="Submit to backend" checked={!!formikBag.values.submitToBackend} value="" onChange={(v,e) => { formikBag.setFieldValue('submitToBackend', e.target.checked); }} />
                        </div>}

                        <div className="form-field submit">
                            {this.state.serverError && <p className="aui-color-text-red submit-error">{this.state.serverError}</p>}
                            <Button type="submit" variant="primary" theme={this.props.theme === "dark" ? "light" : null} isStretched={this.props.submitIsStretched} disabled={disabled || (this.props.recaptcha && !this.state.capVal)} onClick={this.scrollToError}>{submitLabel}</Button>
                        </div>
                    </Form>
                )}
            </Formik>
        );
    }
}

AudiForm.defaultProps = {
    fields: [],
    hiddenFields: {},
    submitLabel: "Submit",
    submitIsStretched: true,
    api: `/1/form`,
    method: "POST",
    onSubmitSuccess: () => {},
    onSubmitError: () => {},
    style: {},
    theme: DEFAULT_THEME
}

export default AudiForm;
